import { useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import loadable from '@loadable/component';
import classNames from 'classnames';

import { Link, Link as LinkAsButton, LinkVariant, RenderType } from '@jsmdg/yoshi';
import { isValueVoucherWithPriceRange } from '../../../shared/product-safeguards';
import { formatPrice } from '../../helper/formatPrice';
import { usePrice } from '../../hooks/usePrice';
import { useProduct } from '../../hooks/useProduct';
import { PurchaseType } from '../../types/enums/purchaseType';
import { FriendsDiscountInfo } from './FriendsDiscountInfo';
import { trackShippingTextClicked } from './price.tracking';
import styles from './Price.module.scss';

const LoadableStrikePriceModal = loadable(async () => import('./StrikePriceModal'));
const LoadableShippingModal = loadable(async () => import('../ShippingModal/ShippingModal'));

type PriceProps = {
    readonly isDisabled?: boolean;
    readonly purchaseType?: string;
};

const messages = defineMessages({
    shippingModalTexLink: {
        defaultMessage: 'zzgl. Versand',
    },
    credit: {
        defaultMessage: 'Guthaben',
    },
    abPrice: {
        defaultMessage: 'ab ',
    },
    a11yOriginalPrice: {
        defaultMessage: 'Streichpreis ',
    },
});

const Price = ({ isDisabled = false, purchaseType }: PriceProps): JSX.Element => {
    const intl = useIntl();
    const product = useProduct();
    const { price } = usePrice();
    const originalPrice = isValueVoucherWithPriceRange(product) ? undefined : product.originalPrice;
    const { hasFriendsDiscount } = product;
    const [isShippingModalOpen, setIsShippingModalOpen] = useState(false);
    const [showStrikePriceModal, setShowStrikePriceModal] = useState(false);

    const handleShippingTextClick = (): void => {
        if (isDisabled) {
            return;
        }

        setIsShippingModalOpen(true);
        trackShippingTextClicked();
    };

    const useFriendsDiscount =
        purchaseType === PurchaseType.Voucher && hasFriendsDiscount && price.multiplier > 1;

    return (
        <>
            <div className="d-flex align-items-center">
                <span
                    data-original-price={!!originalPrice}
                    className={classNames(
                        'theme-text-display-1 mr-1x d-inline-block',
                        styles.price,
                    )}
                    data-testid="price"
                >
                    {formatPrice(intl.locale, Number(price.gross.amount), price.gross.currencyCode)}
                </span>
                {!!originalPrice && (
                    <>
                        <span
                            className={classNames(
                                styles.originalPrice,
                                'mr-0-5x text-decoration-line-through',
                            )}
                        >
                            <span className="screenReaderOnly">
                                {intl.formatMessage(messages.a11yOriginalPrice)}
                            </span>
                            {formatPrice(
                                intl.locale,
                                originalPrice.gross.amount,
                                originalPrice.gross.currencyCode,
                            )}
                        </span>
                        <Link
                            onClick={() => setShowStrikePriceModal(true)}
                            variant={LinkVariant.Brand}
                            className="text-decoration-none"
                            as={RenderType.Button}
                        >
                            *
                        </Link>
                    </>
                )}
            </div>
            {useFriendsDiscount && (
                <FriendsDiscountInfo price={price} multiplier={price.multiplier} />
            )}
            <div
                className={classNames(
                    styles.info,
                    'd-flex text-nowrap theme-text-caption-text mt-md-1x mr-sm-1-5x',
                )}
            >
                <FormattedMessage
                    defaultMessage="{shipping} (inkl. MwSt.)"
                    values={{
                        shipping: (
                            <LinkAsButton
                                onClick={handleShippingTextClick}
                                as={RenderType.Button}
                                variant={LinkVariant.Grey}
                                className={classNames(
                                    styles.shippingHintForPDPRedesign,
                                    'text-nowrap',
                                )}
                            >
                                {intl.formatMessage(messages.shippingModalTexLink)}
                            </LinkAsButton>
                        ),
                    }}
                />
            </div>
            {isShippingModalOpen && (
                <LoadableShippingModal
                    isOpen={isShippingModalOpen}
                    onRequestClose={() => setIsShippingModalOpen(false)}
                />
            )}
            {showStrikePriceModal && (
                <LoadableStrikePriceModal
                    isOpen={showStrikePriceModal}
                    handleOnClose={() => setShowStrikePriceModal(false)}
                    numTickets={price.multiplier}
                />
            )}
        </>
    );
};

export { Price };
